import React from "react";
//#region redux
import { store } from "store";
import { displayErrorMessage } from "store/actions/utilActions";
//#endregion
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { Currency } from "translations/translationKeys";
import { MissingDocumentEnums } from "models/enums";
import { FormHandles } from "models/propTypes/genericFormProps";
import { CommentsFormTypes } from "models/formTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { FormFieldConstants } from "utils/constants";
//endregion
//#region components
import TopTodoCard from "../../_partials/topTodoCard";
import {
  FileViewerContainer,
  GenericCommentsView,
  TitledContainerInner,
  useMountedState,
  EntityCardContent,
} from "components/common";
import { CommentsViewWithInputForm } from "components/common/comments";
import {
  GenericDocumentTodoFooter,
  useGenericDocumentSentBackSnoozeAction,
  useGenericDocumentViewInfo,
} from "components/documents";
import { useFileUploadService, useMissingDocumentService, useGenericDocumentService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const GenericDocumentSentBackTodo: React.FC<TodoCardProps> = (props) => {
  const fileUploadService = useFileUploadService();
  const missingDocumentService = useMissingDocumentService();
  const genericDocumentService = useGenericDocumentService();

  const genericDocument = props.item.genericVoucherSendBack;
  const formRef = React.useRef<FormHandles>(null);
  const [attachments, setAttachments] = React.useState<ImageMeta[]>();
  const { getMounted } = useMountedState();
  const attachmentRequests = React.useRef(new Map<string, Promise<string>>());

  // One request per file, shared; a failed one is dropped so the next call retries instead of failing from cache.
  const getAttachmentData = (attachmentId: string | number) => {
    const key = `${genericDocument!.id}:${attachmentId}`;
    let request = attachmentRequests.current.get(key);
    if (!request) {
      request = genericDocumentService.getAttachmentData(genericDocument!.id, attachmentId);
      request.catch(() => attachmentRequests.current.delete(key));
      attachmentRequests.current.set(key, request);
    }
    return request;
  };

  const snoozeAction = useGenericDocumentSentBackSnoozeAction(genericDocument!.id, props.onActionSuccess);
  const onViewInfoClick = useGenericDocumentViewInfo(genericDocument!.id, formRef);

  React.useEffect(() => {
    if (genericDocument) {
      const attachments =
        genericDocument.images?.map<ImageMeta>((meta) => ({
          id: meta.id ?? 0,
          contentType: meta.contentType,
          fileName: meta.name ?? "",
          numberOfPages: 1,
        })) ?? [];

      setAttachments(attachments);

      genericDocument.images?.forEach((attachment) => {
        getAttachmentData(Number(attachment.id))
          .then((res) => {
            if (getMounted()) {
              setAttachments((prevAttachments) => {
                if (prevAttachments) {
                  const newAttachments = prevAttachments.map((p) => ({ ...p }));
                  const attachmentToUpdate = newAttachments.find((a) => a.id === attachment.id);
                  if (attachmentToUpdate) {
                    attachmentToUpdate.data = res;
                  }
                  return newAttachments;
                } else return prevAttachments;
              });

              if (formRef.current && formRef.current.getValues && formRef.current.setFieldValue) {
                const values: CommentsFormTypes.CommentWithFile = formRef.current.getValues();
                const newFiles = [...(values.filesToUpload ?? [])];
                const file = newFiles.find((x) => x.id === attachment.id);
                if (file) {
                  file.data = res;
                  formRef.current.setFieldValue(FormFieldConstants.Comments.CommentWithFile.FilesToUpload, newFiles);
                }
              }
            }
          })
          .catch(() => undefined);
      });
    }
  }, [genericDocument?.id]);

  const amountType =
    missingDocumentService.getAmountType(genericDocument?.amount ? genericDocument.amount : 0) ===
    MissingDocumentEnums.AmountType.Income
      ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.MissingDocument.Income)
      : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.MissingDocument.Expense);

  const onSubmitGenericDocumentSentBack = () => {
    const onSubmit = async (values: CommentsFormTypes.CommentWithFile): Promise<any> => {
      let files;
      try {
        files = await fileUploadService.updateMissingAttachments(values.filesToUpload ?? [], getAttachmentData);
      } catch (e) {
        store.dispatch(
          displayErrorMessage(
            LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Document.GenericDocumentSentBackTodoAttachmentLoadFailed
            ),
            { showSupportLink: true }
          )
        );
        throw e;
      }

      return genericDocumentService.updateSentBackDocument(
        genericDocument!.id,
        values.explanation,
        files.map(
          (file: { contentType: any; fileName: any; data: any }): WintSuperkollAPIModel.GenericDocumentImage => {
            return {
              contentType: file.contentType,
              name: file.fileName,
              imageData: file.data,
            };
          }
        )
      );
    };

    return (formRef.current && formRef.current.submit(onSubmit)) || new Promise<void>((resolve) => resolve());
  };

  const initialAttachments = React.useMemo(
    () =>
      genericDocument?.images?.map<UploadedFile>(
        (upload): UploadedFile => ({
          ...upload,
          contentType: upload.contentType ?? "",
          fileName: upload.name ?? "",
          id: upload.id,
          fileSize: 0,
          data: upload.imageData ?? "",
        })
      ),
    [genericDocument]
  );

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={`${genericDocument?.amount ? genericDocument.amount.toString() : "0"} ${Currency.SEK}`}
          descriptionHeadingTwo={genericDocument?.description}
          descriptionheadingThree={`${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Common.Type
          )}: ${amountType}`}
          footerText={`${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Todos.TodoCardSubTypeText_GenericVouchersSentBack
          )}: ${LocalizationHelperInstance.getFormattedDate(genericDocument!.updatedDate)}`}
          quickActions={[snoozeAction]}
          onViewAllClick={onViewInfoClick}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Document.DocumentSentBackTodoMainActionCardHeaderText
          )}
          subHeader={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Document.DocumentSentBackTodoMainActionCardDescription
          )}
        >
          <CommentsViewWithInputForm
            ref={formRef}
            parentId={props.item.id}
            commentType={WintSuperkollAPIModel.GenericCommentType.GenericVoucher}
            initialValues={{
              filesToUpload: initialAttachments,
              postWithoutPicture: false,
            }}
            onUploadedFilesChangeHandler={(name: string, updatedValue: UploadedFile[]) => {
              setAttachments(
                updatedValue.map<ImageMeta>((upload) => ({
                  ...upload,
                  id: upload.id as number,
                  numberOfPages: 1,
                }))
              );
            }}
          />
        </TitledContainerInner>
      }
      infoViewer={
        attachments === undefined || attachments.length > 0 ? (
          <FileViewerContainer
            key={genericDocument?.id}
            meta={attachments}
            loading={attachments === undefined}
            getFile={getAttachmentData}
          />
        ) : undefined
      }
      footer={
        <GenericDocumentTodoFooter
          genericDocument={genericDocument!}
          markAsDoneHandler={props.onActionSuccess}
          onTodoClear={onSubmitGenericDocumentSentBack}
        />
      }
    >
      <GenericCommentsView
        parentId={genericDocument!.id}
        type={WintSuperkollAPIModel.GenericCommentType.GenericVoucher}
        collapsed
      />
    </EntityCardContent>
  );
};

export default GenericDocumentSentBackTodo;
